import { Component, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LoggingService, TdoeButtonDirective } from '@tdoe/design-system';
import { Pagination, Sorting } from 'app/dto';
import { ClassService } from 'app/services/class/class.service';
import { BehaviorSubject, combineLatest, firstValueFrom, map, mergeMap, Observable, tap } from 'rxjs';
import { AdditionalInfoComponent } from '../shared/additional-info/additional-info.component';
import { AdditionalInfoModel, Category, Field } from 'app/services/additional-info/additional-info.model';
import { MatExpansionModule } from '@angular/material/expansion';
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { DayOfWeek } from 'app/models/dayOfWeek';
import { getNestedProperty, ObjectUtilities } from 'app/utilities/object-utilities/object-utilities';
import { Title } from '@angular/platform-browser';
import { MatTableModule } from '@angular/material/table';
import { TdoeBoolDisplayPipe } from 'app/pipes/bool-display/tdoe-bool-display.pipe';
import { MatMenuModule } from '@angular/material/menu';
import { downloadExcelDocument } from 'app/utilities/excel-export-utilities/excel-export-utilities';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { toObservable } from '@angular/core/rxjs-interop';
import { NestedPropertyPipe } from 'app/pipes/nested-property/nested-property.pipe';
@Component({
  selector: 'app-class-view',
  standalone: true,
  imports: [
    MatIconModule,
    AdditionalInfoComponent,
    MatExpansionModule,
    DatePipe,
    MatButtonToggleModule,
    AsyncPipe,
    CommonModule,
    RouterModule,
    TdoeButtonDirective,
    MatTableModule,
    TdoeBoolDisplayPipe, 
    MatMenuModule,
    MatProgressBarModule,
    NgxSkeletonLoaderModule,
    MatPaginatorModule,
    MatSortModule,
    NestedPropertyPipe
  ],
  templateUrl: './class-view.component.html',
  styleUrl: './class-view.component.scss'
})
export class ClassViewComponent {

  public AdditionalInfoContextKey = 'ClassViewComponent';
  public isExporting: boolean = false;

  // #region D.I.
  private readonly classService = inject(ClassService);
  private readonly logger = inject(LoggingService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  // #endregion

  private readonly categoriesSubject = new BehaviorSubject<AdditionalInfoModel.Category[]>([]);
  private readonly categories$: Observable<AdditionalInfoModel.Category[]> = this.categoriesSubject.asObservable();
  private readonly classId = this.route.snapshot.paramMap.get('id') ?? '';
  private readonly class$ = firstValueFrom(this.classService.getClass(this.classId));
  public readonly classLoading$ = new BehaviorSubject<boolean>(true);

  public daysOfWeek = DayOfWeek;
  private boolPipe = new TdoeBoolDisplayPipe();


  public resolver(obj: any, key: string): any {
    return ObjectUtilities.getNestedProperty(obj, key);
  }

  public additionalInfoFields: Category[] = [
    {
      name: 'Student',
      expanded: true,
      isArray: true,
      jsonPath: '$.additionalInfo.students',
      fields: [
        {
          name: 'Student Last',
          key: 'nameLast',
          selected: false,
          exportColumn: {
            jsonPath: '$.nameLast'
          }
        },
        {
          name: 'Student First',
          key: 'nameFirst',
          selected: false,
          exportColumn: {
            jsonPath: '$.nameFirst'
          }
        },
        {
          name: 'Student Middle',
          key: 'nameMiddle',
          selected: false,
          exportColumn: {
            jsonPath: '$.nameMiddle'
          }
        },
        {
          name: 'State ID',
          key: 'ssid',
          selected: false,
          exportColumn: {
            jsonPath: '$.ssid'
          }
        },
        {
          name: 'EdFi ID',
          key: 'stateStudentId',
          selected: false,
          exportColumn: {
            jsonPath: '$.edFi_Id'
          }
        },
        {
          name: 'Grade',
          key: 'grade',
          selected: false,
          exportColumn: {
            jsonPath: '$.grade'
          }
        },
        {
          name: 'Date of Birth',
          key: 'dateOfBirth',
          selected: false,
          exportColumn: {
            jsonPath: '$.dateOfBirth'
          }
        },
        {
          name: 'Student Class Assignment Begin Date',
          key: 'assignmentPeriodStartDate',
          selected: false,
          exportColumn: {
            jsonPath: '$.assignmentPeriodStartDate'
          }
        },
        {
          name: 'Student Class Assignment End Date',
          key: 'assignmentPeriodEndDate',
          selected: false,
          exportColumn: {
            jsonPath: '$.assignmentPeriodEndDate'
          }
        }
      ],
      valuePath: '$.additionalInfo.Students'
    },
    {
      name: 'Staff',
      expanded: true,
      isArray: true,
      jsonPath: '$.additionalInfo.staff',
      fields: [
        {
          name: 'Staff Last',
          key: '_nameLast_',
          selected: false,
          exportColumn: {
            jsonPath: '$.NameLast'
          }
        },
        {
          name: 'Staff First',
          key: '_nameFirst_',
          selected: false,
          exportColumn: {
            jsonPath: '$.NameFirst'
          }
        },
        {
          name: 'Staff Middle',
          key: '_nameMiddle_',
          selected: false,
          exportColumn: {
            jsonPath: '$.NameMiddle'
          }
        },
        {
          name: 'Staff TLN',
          key: 'teacherLicenseNumber',
          selected: false,
          exportColumn: {
            jsonPath: '$.TeacherLicenseNumber'
          }
        },
        {
          name: 'Teacher of Record',
          key: 'teacherOfRecord',
          selected: false,
          exportColumn: {
            jsonPath: '$.TeacherLicenseNumber'
          }
        },
        {
          name: 'Staff Class Assignment Begin Date',
          key: 'assignmentBeginDate',
          selected: false,
          exportColumn: {
            jsonPath: '$.assignmentBeginDate'
          }
        },
        {
          name: 'Staff Class Assignment End Date',
          key: 'assignmentEndDate',
          selected: false,
          exportColumn: {
            jsonPath: '$.assignmentEndDate'
          }
        },
        {
          name: 'Staff Organization Email',
          key: 'email',
          selected: false,
          exportColumn: {
            jsonPath: '$.email'
          }
        }
      ],
      valuePath: '$.additionalInfo.Staff'
    }
  ];

  private readonly selectedCategories: Observable<AdditionalInfoModel.Category[]> =
    combineLatest([this.categories$])
      .pipe(
        map(([categories]) => {
          return categories
            .filter(category => category.fields.some(field => field.selected))
            .map(category => ({
              ...category,
              fields: category.fields
                .filter(field => field.selected)
                .map(field => ({
                  ...field
                }))
            }));
        }),
        tap(_ => this.logger.debug('ClassViewComponent -> selectedCategories', { data: {
          _
        } }))
      );

  //#region Students

  private readonly studentPaging = signal<Pagination>({ pageIndex: 0, pageSize: 50 });
  private readonly studentSorting = signal<Sorting>({ sortColumn: 'nameLast', sortDirection: 'asc' });
  public readonly studentLoading = signal<boolean>(true);

  public readonly studentVM$ = combineLatest([
    toObservable(this.studentPaging),
    toObservable(this.studentSorting),
    toObservable(this.studentLoading),
    this.class$
  ]).pipe(
    map(([
      paging,
      sorting,
      loading,
      course
    ]) => ({
      paging,
      sorting,
      loading,
      course
    })),
    mergeMap(_ => this.classService.getStudent(_.course.id!, _.paging, _.sorting)),
    tap(_ => this.studentLoading.set(false))
  );

  public pageStudents(e: PageEvent): void {
    this.studentLoading.set(true);
    this.studentPaging.set({
      pageIndex: e.pageIndex,
      pageSize: e.pageSize
    });
  }

  //#endregion

  //#region Staff

  private readonly staffPaging = signal<Pagination>({ pageIndex: 0, pageSize: 50 });
  private readonly staffSorting = signal<Sorting>({ sortColumn: 'nameLast', sortDirection: 'asc' });
  public readonly staffLoading = signal<boolean>(true);

  public readonly staffVM$ = combineLatest([
    toObservable(this.staffPaging),
    toObservable(this.staffSorting),
    toObservable(this.staffLoading),
    this.class$
  ]).pipe(
    map(([
      paging,
      sorting,
      loading,
      course
    ]) => ({
      paging,
      sorting,
      loading,
      course
    })),
    mergeMap(_ => this.classService.getStaff(_.course.id!, _.paging, _.sorting)),
    tap(_ => this.staffLoading.set(false))
  );

  public pageStaff(e: PageEvent): void {
    this.staffLoading.set(true);
    this.staffPaging.set({
      pageIndex: e.pageIndex,
      pageSize: e.pageSize
    });
  }

  //#endregion

  public readonly viewModel$ = combineLatest([
    this.selectedCategories,
    this.class$
  ])
    .pipe(
      map(([selectedCategories,
            classDetail
          ]) => ({
            selectedCategories,
            classDetail
          })),
      map(data => ({
        selectedCategories: data.selectedCategories,
        classDetail: data.classDetail,
        staffColumns: this.getCategory(data.selectedCategories, 'Staff')?.fields.map(_ => this.strip(_.key)),
        studentColumns: this.getCategory(data.selectedCategories, 'Student')?.fields.map(_ => this.strip(_.key)),
        staffCategory: this.getCategory(data.selectedCategories, 'Staff'),
        studentCategory: this.getCategory(data.selectedCategories, 'Student')
      })),
      tap(_ => {
        this.logger.debug('ClassViewComponent -> viewModel$', { data: { _ } });
        this.classLoading$.next(false);
      })
    );

  public getCategory(categories: Category[], categoryName: string): Category | undefined {
    return categories.find(_ => _.name === categoryName && _.fields.some(field => field.selected));
  }

  public strip(val: string): string {
    return val.replace(new RegExp(/_/, 'g'), '');
  }

  public onFieldSelected(e: Category[]): void {
    this.logger.debug('ClassViewComponent -> onFieldSelected', { data: { e } });
    this.categoriesSubject.next(e);
  }

  public async export(id: string, additonalInfo?: Category[]): Promise<void> {
    this.isExporting = true;
    try {
      this.logger.debug('ClassViewComponent -> export', {
        data: { id, additonalInfo }
      });
      const file = await firstValueFrom(this.classService.exportPdf(id, additonalInfo));
      downloadExcelDocument(file);
    } finally {
      this.isExporting = false;
    }
  }

  public resolveNestedProperty(student: unknown, col: Field): unknown {
    let result = getNestedProperty(student, col.valuePath ?? col.key);

    if (typeof result == 'boolean') {
      result = this.boolPipe.transform(result, 'Yes', 'No');
    }

    return result;
  }

}
